import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

type IGarbageContext = {
  registerCollector: (key: string, collector: () => void) => void
  registerUrlException: (urlException: string) => void
  runCollector: (key: string) => void
  purge: () => void
  purgeUrlExceptions: () => void
}

const GarbageContext = createContext<IGarbageContext>({
  registerCollector: (key: string, collector: () => void) => null,
  registerUrlException: (urlException: string) => null,
  runCollector: (key: string) => null,
  purge: () => null,
  purgeUrlExceptions: () => null
})

export const GarbageProvider = ({ children }: any) => {
  const { pathname } = useLocation()
  const [path, setPath] = useState<string | undefined>()
  const [urlExceptions, setUrlExceptions] = useState<string[]>([])
  const [collectors, setCollectors] = useState<{ key: string; collector: () => void }[]>([])

  const runCollectors = () => {
    for (let i = 0; i < collectors.length; i++) {
      const { key, collector } = collectors[i]

      console.log('Running garbage collection on: ', key)

      collector()
    }
    setUrlExceptions([])
  }

  useEffect(() => {
    if (!path) {
      setPath(pathname)
    } else if (pathname !== path) {
      if (!urlExceptions.find((e) => pathname.startsWith(e))) {
        runCollectors()
      }
    }
  }, [pathname])

  const state = useMemo(
    () => ({
      registerCollector: (key: string, collector: () => void) => {
        const cloned = [...collectors]
        const index = cloned.findIndex((c) => c.key === key)

        if (index === -1) {
          setCollectors([...cloned, { key, collector }])
        } else {
          cloned[index] = { key, collector }
          setCollectors([...cloned])
        }
      },
      registerUrlException: (urlException: string) => {
        const cloned = [...urlExceptions]
        const index = cloned.findIndex((e) => e === urlException)

        if (index === -1) {
          setUrlExceptions([...cloned, urlException])
        }
      },
      runCollector: (key: string) => {
        const cls = collectors.find((c) => c.key === key)

        console.log('RUNNING COLLECTOR', key)

        if (cls) {
          cls.collector()
          setCollectors([...collectors.filter((c) => c.key !== key)])
        }
      },
      purge: () => {
        setCollectors([])
      },
      purgeUrlExceptions: () => {
        setUrlExceptions([])
      }
    }),
    [collectors]
  )

  return <GarbageContext.Provider value={state}>{children}</GarbageContext.Provider>
}

export const useGarbageCollector = () => useContext(GarbageContext)
